import { useState, useCallback, useMemo } from "react";
import { useLazyGetAppointmentsByCompanyAndMonthQuery } from "../../../services/api/utils.api";
import { IAppointment } from "../types/appointments.types";

interface UseAppointmentsDataParams {
  companyId: number;
  fakeCompanyId?: number;
}

interface MonthKey {
  year: number;
  month: number;
}

/**
 * Hook for managing appointments data with smart month-based caching
 *
 * Loads appointments by month and caches them to avoid redundant API calls.
 * Automatically fetches new months as the user navigates through the calendar.
 */
export const useAppointmentsData = ({
  companyId,
  fakeCompanyId,
}: UseAppointmentsDataParams) => {
  const [loadedMonths, setLoadedMonths] = useState<Set<string>>(new Set());
  const [appointmentsCache, setAppointmentsCache] = useState<
    Map<string, IAppointment[]>
  >(new Map());
  const [isLoading, setIsLoading] = useState(false);

  const [fetchAppointments] = useLazyGetAppointmentsByCompanyAndMonthQuery();

  // Generate month key for caching
  const getMonthKey = useCallback((year: number, month: number): string => {
    return `${year}-${month}`;
  }, []);

  // Parse month key back to year and month
  const parseMonthKey = useCallback((key: string): MonthKey => {
    const [year, month] = key.split("-").map(Number);
    return { year, month };
  }, []);

  // Load appointments for a specific month
  const loadMonth = useCallback(
    async (year: number, month: number) => {
      const monthKey = getMonthKey(year, month);

      // Skip if already loaded
      if (loadedMonths.has(monthKey)) {
        return;
      }
      //
      setIsLoading(true);
      try {
        const result = await fetchAppointments({
          companyId,
          month,
          year,
          fakeCompanyId,
        }).unwrap();

        // Transform backend response to frontend format
        const transformedAppointments: IAppointment[] = result.map(
          (apt: any) => {
            // Parse date without timezone conversion
            let dateStr = "";
            if (apt.appointmentDate) {
              const dateMatch = apt.appointmentDate.match(
                /^(\d{4})-(\d{2})-(\d{2})/,
              );
              if (dateMatch) {
                dateStr = `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
              }
            }

            return {
              id: apt.id,
              patientId: apt.usersId || 0,
              usersId: apt.usersId || 0,
              patientName: apt.patientName || "",
              usersGuid: apt.usersGuid,
              patientFakeCompanyId: apt.patientFakeCompanyId,
              patientFakeCompanyName: apt.patientFakeCompanyName || "",
              therapistId: apt.therapistsId || 0,
              therapistsId: apt.therapistsId || 0,
              therapistName: apt.therapistName || "",
              therapistsGuid: apt.therapistsGuid,
              companyId: apt.companyId,
              companyName: apt.companyName || "",
              date: dateStr,
              time: apt.startTime || "",
              endTime: apt.endTime || "",
              duration:
                apt.startTime && apt.endTime
                  ? calculateDuration(apt.startTime, apt.endTime)
                  : 60,
              mediaId: apt.mediaId,
              mediaName: apt.mediaName || "",
              notes: apt.appointmentReport || "",
              comments: apt.comments || "",
              statusId: apt.statusId,
              treatmentGroupId: apt.treatmentGroupId,
              treatmentGroupName: apt.treatmentGroupName,
              appointmentsProgramsId: apt.appointmentsProgramsId,
              fundingSourcesId: apt.fundingSourcesId,
              fundingSourceName: apt.fundingSourceName || "",
              // Treatment room details
              treatmentRoomId: apt.treatmentRoomId,
              treatmentRoomName: apt.treatmentRoomName || "",
              // Treatment program details
              treatmentProgramName: apt.treatmentProgramName,
              treatmentProgramTotalSessions: apt.treatmentProgramTotalSessions,
              treatmentProgramCurrentSession:
                apt.treatmentProgramCurrentSession,
              treatmentProgramSequenceId: apt.treatmentProgramSequenceId,
              treatmentTypeId: apt.treatmentTypeId,
              treatmentTypeName: apt.treatmentTypeName,
              hasActionReport: apt.hasActionReport ?? false,
              actionReportScoreUsersId: apt.actionReportScoreUsersId ?? undefined,
              hasTherapistAnswered: apt.hasTherapistAnswered ?? false,
              seriesId: apt.seriesId ?? undefined,
              device: apt.device ?? null,
            };
          },
        );

        // Update cache
        setAppointmentsCache((prev) => {
          const newCache = new Map(prev);
          newCache.set(monthKey, transformedAppointments);
          return newCache;
        });

        // Mark month as loaded
        setLoadedMonths((prev) => new Set(prev).add(monthKey));
      } catch (error) {
        console.error(
          `Error loading appointments for ${year}-${month}:`,
          error,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [companyId, fakeCompanyId, fetchAppointments, getMonthKey, loadedMonths],
  );

  // Calculate duration in minutes from time strings
  const calculateDuration = (startTime: string, endTime: string): number => {
    const [startHours, startMinutes] = startTime.split(":").map(Number);
    const [endHours, endMinutes] = endTime.split(":").map(Number);
    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    return endTotalMinutes - startTotalMinutes;
  };

  // Get all appointments from cache
  const allAppointments = useMemo(() => {
    const appointments: IAppointment[] = [];
    appointmentsCache.forEach((monthAppointments) => {
      appointments.push(...monthAppointments);
    });
    return appointments;
  }, [appointmentsCache]);

  // Helper to format date as YYYY-MM-DD without timezone issues
  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Filter appointments by date range
  const filterByDateRange = useCallback(
    (start: Date, end: Date): IAppointment[] => {
      const startStr = formatDateString(start);
      const endStr = formatDateString(end);

      return allAppointments.filter((apt) => {
        return apt.date >= startStr && apt.date <= endStr;
      });
    },
    [allAppointments],
  );

  // Filter by therapist
  const filterByTherapist = useCallback(
    (therapistId?: number): IAppointment[] => {
      if (!therapistId) return allAppointments;
      return allAppointments.filter((apt) => apt.therapistsId === therapistId);
    },
    [allAppointments],
  );

  // Filter by patient search
  const filterByPatientSearch = useCallback(
    (searchTerm?: string): IAppointment[] => {
      if (!searchTerm) return allAppointments;
      const lowerSearch = searchTerm.toLowerCase();
      return allAppointments.filter((apt) =>
        apt.patientName.toLowerCase().includes(lowerSearch),
      );
    },
    [allAppointments],
  );

  // Combined filter function
  const filterAppointments = useCallback(
    (options: {
      dateRange?: { start: Date; end: Date };
      fakeCompanyIds?: number[];
      therapistId?: number;
      therapistIds?: number[];
      fundingSourceIds?: number[];
      patientSearch?: string;
    }): IAppointment[] => {
      let filtered = allAppointments;

      if (options.dateRange) {
        const startStr = formatDateString(options.dateRange.start);
        const endStr = formatDateString(options.dateRange.end);
        filtered = filtered.filter(
          (apt) => apt.date >= startStr && apt.date <= endStr,
        );
      }

      // Filter by sub-companies (fake companies)
      if (options.fakeCompanyIds && options.fakeCompanyIds.length > 0) {
        filtered = filtered.filter((apt) => {
          // If no fakeCompanyId, include only if 0 is in the filter (meaning "All")
          if (!apt.patientFakeCompanyId) {
            return (
              options.fakeCompanyIds!.includes(0) ||
              options.fakeCompanyIds!.includes(-1)
            );
          }
          return options.fakeCompanyIds!.includes(apt.patientFakeCompanyId);
        });
      }

      // Support both single therapistId and multiple therapistIds
      if (options.therapistIds && options.therapistIds.length > 0) {
        filtered = filtered.filter((apt) =>
          options.therapistIds!.includes(apt.therapistsId || 0)
        );
      } else if (options.therapistId) {
        filtered = filtered.filter(
          (apt) => apt.therapistsId === options.therapistId,
        );
      } else {
        // No therapist filter active – hide "other" appointments (they only make sense in a therapist column)
        filtered = filtered.filter((apt) =>
          !!(apt.patientName || apt.treatmentGroupName || apt.mediaId)
        );
      }

      // Filter by funding sources
      if (options.fundingSourceIds && options.fundingSourceIds.length > 0) {
        filtered = filtered.filter((apt) => {
          // If appointment has no funding source (no treatment program),
          // include only if 0 is in the filter (meaning "No Funding Source")
          if (!apt.fundingSourcesId) {
            return options.fundingSourceIds!.includes(0);
          }
          return options.fundingSourceIds!.includes(apt.fundingSourcesId);
        });
      }

      if (options.patientSearch) {
        const lowerSearch = options.patientSearch.toLowerCase();
        filtered = filtered.filter((apt) =>
          apt.patientName.toLowerCase().includes(lowerSearch),
        );
      }

      return filtered;
    },
    [allAppointments],
  );

  // Ensure a month is loaded (call this when navigating)
  const ensureMonthLoaded = useCallback(
    async (date: Date) => {
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // JavaScript months are 0-indexed
      await loadMonth(year, month);
    },
    [loadMonth],
  );

  // Ensure a date range is loaded (for week view spanning months)
  const ensureDateRangeLoaded = useCallback(
    async (start: Date, end: Date) => {
      const startYear = start.getFullYear();
      const startMonth = start.getMonth() + 1;
      const endYear = end.getFullYear();
      const endMonth = end.getMonth() + 1;

      // Load start month
      await loadMonth(startYear, startMonth);

      // Load end month if different
      if (startYear !== endYear || startMonth !== endMonth) {
        await loadMonth(endYear, endMonth);
      }
    },
    [loadMonth],
  );

  // Reload a specific month (clear cache and refetch)
  const reloadMonth = useCallback(
    async (date: Date) => {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const monthKey = getMonthKey(year, month);

      // Clear from loaded months set
      setLoadedMonths((prev) => {
        const newSet = new Set(prev);
        newSet.delete(monthKey);
        return newSet;
      });

      // Clear from cache
      setAppointmentsCache((prev) => {
        const newCache = new Map(prev);
        newCache.delete(monthKey);
        return newCache;
      });

      // Reload the month
      await loadMonth(year, month);
    },
    [getMonthKey, loadMonth],
  );

  return {
    allAppointments,
    isLoading,
    loadedMonths: Array.from(loadedMonths),
    ensureMonthLoaded,
    ensureDateRangeLoaded,
    reloadMonth,
    filterByDateRange,
    filterByTherapist,
    filterByPatientSearch,
    filterAppointments,
  };
};
